/* eslint-disable no-bitwise */
import { cloneDeep } from 'lodash'
import AnalysisType, { AnalysisTypeStrings } from '../enums/AnalysisType'
import CountType, { CountTypeStrings } from '../enums/CountType'
import FilterVo from '../vo/Filter'
import LxnxFilterVo from '../vo/LxnxFilter'
import DateRangeVo from '../vo/DateRange'
import TimeInterval, { TimeIntervalStrings } from '../enums/TimeInterval'
import { getMockNow } from './featureUtil'
import { addUserTzInterval, getIntervalObj } from './dateUtil'
// import { getContentType } from './analysisTypeUtil'
// import ThemeInfoVo from '../vo/ThemeInfo'
import { getIds } from './themeInfoUtil'
import AnnotationDisplayMode from '../enums/AnnotationDisplayMode'
import { DateRange } from '../schemas/DateRangeSchema'
import { TimeZoneStrings } from '../enums/TimeZone'
import { LogInfo } from '../schemas/LogInfoSchema'
import { TopicInfo } from '../schemas/TopicInfoSchema'
import { Filter } from '../schemas/FilterSchema'
import { DashboardWidgetType } from '../features/dashboard/types'

const MS_IN_MINUTE = 60 * 1000
const MS_IN_HOUR = 60 * 60 * 1000

export function getDefaultCountTypeByAnalysisType(
  analysisType: AnalysisTypeStrings | undefined
): CountTypeStrings {
  switch (analysisType) {
    case AnalysisType.SW_DEMOGRAPHIC:
    case AnalysisType.AM_DEMOGRAPHIC:
    case AnalysisType.SW_BENCHMARK:
    case AnalysisType.AM_BENCHMARK:
    case AnalysisType.SW_GEOGRAPHY:
    case AnalysisType.AM_GEOGRAPHY:
    case AnalysisType.AM_THINGS:
    case AnalysisType.AM_CONVERSATION:
    case AnalysisType.AM_SENTIMENT:
    case AnalysisType.SW_POST_ACTIVITY:
    case AnalysisType.AM_POST_ACTIVITY:
    // Currently A3D Sentiment widget provides post-based option, but
    // Net Sentiment is actually counted with SentenceObj.
    // We use it because for twitter contents it's the same as counting with
    // DocumentObj; if twitter is not the only data source we need platform
    // team's support to draw net sentiment spark line with DocumentObj count.
    // eslint-disable-next-line no-fallthrough
    case AnalysisType.AM_LIKES_AND_DISLIKES:
    case AnalysisType.SW_LANGUAGE:
    case AnalysisType.AM_LANGUAGE:
    case AnalysisType.AM_AUTHOR:
    case AnalysisType.SW_TAGS:
    case AnalysisType.SW_TRENDING:
    case AnalysisType.AM_TRENDING:
      return CountType.POSTS
    default:
      return CountType.MENTIONS
  }
}

export function deriveCountType(widgetSetting: {
  countType?: CountTypeStrings
  analysisType?: AnalysisTypeStrings
}): CountTypeStrings {
  if (widgetSetting.countType) {
    return widgetSetting.countType
  }

  return getDefaultCountTypeByAnalysisType(widgetSetting.analysisType)
}

export function isMultipleTopicsWidgets(analysisType: AnalysisTypeStrings): boolean {
  return [
    AnalysisType.SW_CROSSTAB,
    AnalysisType.AM_CROSSTAB,
    AnalysisType.SW_TIMELINE_COMPARISON,
    AnalysisType.AM_TIMELINE_COMPARISON,
    AnalysisType.SW_TOPIC_COMPARISON,
    AnalysisType.AM_TOPIC_COMPARISON,
    AnalysisType.SW_BRAND_PASSION_INDEX,
    AnalysisType.SW_SIDE_BY_SIDE
  ].includes(analysisType)
}

export interface WidgetInfo {
  analysisType: AnalysisTypeStrings
  topicInfos?: TopicInfo[]
  selectedTopic?: TopicInfo
}

export function getSearchTopics(widget: WidgetInfo): TopicInfo[] {
  if (!widget) return []
  if (isMultipleTopicsWidgets(widget.analysisType)) return widget.topicInfos || []
  if (widget.selectedTopic) return [widget.selectedTopic]
  return widget.selectedTopic ? [widget.selectedTopic] : []
}

export function getSearchTopicsFromProWidget(
  proWidget: { selectedTopic?: TopicInfo } | undefined
): TopicInfo[] {
  if (!proWidget) return []
  // TODO: pro.util.getTopicsFromAnalysis
  // const { selectedAnalysis } = widget
  // if (!!selectedAnalysis) {
  //   return nb.ui.pro.util.getTopicsFromAnalysis(selectedAnalysis)
  // }
  return proWidget.selectedTopic ? [proWidget.selectedTopic] : []
}

export function adjustTime(time: number, unit: number, isForward: boolean): Date {
  const remain = time % unit
  let adjustedTime = time - remain
  if (isForward && remain !== 0) {
    adjustedTime += unit
  }

  const value = new Date()
  value.setTime(adjustedTime)
  return value
}

export function getTimeShiftByType(
  interval: TimeIntervalStrings,
  original: Date,
  timeZone?: TimeZoneStrings
): Date {
  let ret = new Date()
  switch (interval) {
    case TimeInterval.LAST_HOUR:
      ret = addUserTzInterval(original, getIntervalObj(0, 0, 0, -1), timeZone)
      break
    case TimeInterval.LAST_DAY:
      ret = addUserTzInterval(original, getIntervalObj(0, 0, -1), timeZone)
      break
    case TimeInterval.LAST_WEEK:
      ret = addUserTzInterval(original, getIntervalObj(0, 0, -7), timeZone)
      break
    case TimeInterval.LAST_MONTH:
      ret = addUserTzInterval(original, getIntervalObj(0, -1), timeZone)
      break
    case TimeInterval.LAST_QUARTER:
      ret = addUserTzInterval(original, getIntervalObj(0, -3), timeZone)
      break
    case TimeInterval.LAST_SIX_MONTHS:
      ret = addUserTzInterval(original, getIntervalObj(0, -6), timeZone)
      break
    case TimeInterval.LAST_YEAR:
      ret = addUserTzInterval(original, getIntervalObj(-1), timeZone)
      break
    case TimeInterval.LAST_15MONTHS:
      ret = addUserTzInterval(original, getIntervalObj(0, -15), timeZone)
      break
    case TimeInterval.LAST_18MONTHS:
      ret = addUserTzInterval(original, getIntervalObj(0, -18), timeZone)
      break
    case TimeInterval.LAST_21MONTHS:
      ret = addUserTzInterval(original, getIntervalObj(0, -21), timeZone)
      break
    case TimeInterval.LAST_2YEARS:
      ret = addUserTzInterval(original, getIntervalObj(-2), timeZone)
      break
    case TimeInterval.LAST_27MONTHS:
      ret = addUserTzInterval(original, getIntervalObj(0, -27), timeZone)
      break
    case TimeInterval.LAST_51MONTHS:
      ret = addUserTzInterval(original, getIntervalObj(0, -51), timeZone)
      break
    case TimeInterval.ALL:
      ret.setTime(0)
      break
    case TimeInterval.OPEN_END:
    case TimeInterval.CUSTOM:
      break
    default:
      throw new Error(`Unknown TimeInterval: ${interval}`)
  }
  return ret
}

export function getDateRangeByType(
  interval: TimeIntervalStrings,
  now?: Date,
  from?: Date,
  to?: Date,
  timeZone?: TimeZoneStrings
): Required<DateRange> {
  const currentDate = now || new Date()
  if (getMockNow()) {
    const mockNow = +getMockNow()
    currentDate.setTime(mockNow)
  }
  const unit = interval === TimeInterval.LAST_HOUR ? MS_IN_MINUTE : MS_IN_HOUR
  let end = adjustTime(currentDate.getTime(), unit, true)
  let start = adjustTime(currentDate.getTime(), unit, false)
  start = getTimeShiftByType(interval, start, timeZone)

  switch (interval) {
    case TimeInterval.OPEN_END:
      start = from || start
      break
    case TimeInterval.CUSTOM:
      start = from || start
      end = to || end
      break
    default:
      break
  }

  const dateRange = DateRangeVo()
  dateRange.start = start.getTime()
  dateRange.end = end.getTime()
  return dateRange as Required<DateRange>
}

export function toFilter(setting: DashboardWidgetType, timeZone?: TimeZoneStrings) {
  const now = new Date()
  const filter = FilterVo()
  filter.lxnxFilter = LxnxFilterVo()
  let dateRange = DateRangeVo()
  if (TimeInterval.CUSTOM === setting.timeInterval) {
    dateRange = getDateRangeByType(
      setting.timeInterval,
      now,
      new Date(setting.startDatetime!),
      new Date(setting.endDatetime!),
      timeZone
    )
  } else if (TimeInterval.ALL === setting.timeInterval) {
    const topics = getSearchTopics(setting as Required<DashboardWidgetType>)
    dateRange.start = new Date().getTime()
    const end = new Date()
    end.setTime(0)
    dateRange.end = end.getTime()

    for (let i = 0; i < topics.length; i += 1) {
      const topicDateRange = getDateRangeByType(
        topics[i].timeInterval!,
        now,
        // TODO: should instead convert fromDate/toDate to number from schema
        // @ts-ignore
        new Date(parseInt(topics[i]!.fromDate!, 10)),
        // @ts-ignore
        new Date(parseInt(topics[i]!.toDate!, 10)),
        timeZone
      )

      if (topicDateRange.start < dateRange.start) {
        dateRange.start = new Date(topicDateRange.start).getTime()
      }
      if (topicDateRange.end > dateRange.end) {
        dateRange.end = new Date(topicDateRange.end).getTime()
      }
    }
  } else {
    dateRange = getDateRangeByType(setting.timeInterval!)
  }
  if (!!setting.includedUserTags && setting.includedUserTags.length > 0) {
    filter.includedUserTags = cloneDeep(setting.includedUserTags)
  }
  if (!!setting.excludedUserTags && setting.excludedUserTags.length > 0) {
    filter.excludedUserTags = cloneDeep(setting.excludedUserTags)
  }
  if (!!setting.annotations && setting.annotations.length > 0) {
    let mask = 0
    for (let i = 0; i < setting.annotations.length; i += 1) {
      switch (setting.annotations[i]) {
        case AnnotationDisplayMode.ANNOTATED:
          mask |= 1
          break
        case AnnotationDisplayMode.UNANNOTATED:
          mask |= 2
          break
        case AnnotationDisplayMode.ORIGINAL:
          mask |= 4
          break
        default:
          break
      }
    }
    switch (mask) {
      case 1:
        filter.display = AnnotationDisplayMode.ANNOTATED
        break
      case 2:
        filter.display = AnnotationDisplayMode.UNANNOTATED
        break
      case 3:
        break
      default:
        break
    }
  }
  filter.publishedDate = dateRange

  if (setting.sentiments) {
    filter.sentiments = [setting.sentiments.slice()]
  }

  if (setting.genders) {
    filter.genders = setting.genders.slice()
  }

  if (setting.sources) {
    filter.sources = setting.sources.slice()
  }

  if (setting.includedDomains) {
    filter.domains = setting.includedDomains.slice()
  }

  if (setting.excludedDomains) {
    filter.excludedDomains = setting.excludedDomains.slice()
  }

  if (setting.includedAuthors) {
    filter.includedAuthors = JSON.parse(JSON.stringify(setting.includedAuthors))
  }

  if (setting.excludedAuthors) {
    filter.excludedAuthors = JSON.parse(JSON.stringify(setting.excludedAuthors))
  }

  if (setting.includedPosts) {
    filter.includedPosts = cloneDeep(setting.includedPosts)
  }

  if (setting.excludedPosts) {
    filter.excludedPosts = cloneDeep(setting.excludedPosts)
  }

  if (setting.includedLanguages) {
    filter.includedLanguages = setting.includedLanguages.slice()
  }

  if (setting.excludedLanguages) {
    filter.excludedLanguages = setting.excludedLanguages.slice()
  }

  if (setting.includedInterests) {
    filter.includedInterests = [setting.includedInterests.slice()]
  }

  if (setting.excludedInterests) {
    filter.excludedInterests = [setting.excludedInterests.slice()]
  }

  if (setting.includedTerms) {
    const includedKeywords = []
    for (let i = 0; i < setting.includedTerms.length; i += 1) {
      if (setting.includedTerms[i].terms) {
        includedKeywords.push(setting.includedTerms[i].terms)
      }
    }
    filter.includedKeywords = includedKeywords
  }

  // FIXME: Not sure the impact, but some extra properties `widgetSetting.excludedTerms` that weren't exsit originally
  // originally this field is lost during `nb.dto.clone(dashboard)` process in `onSuccess_getDashboard_` in show.js
  if (setting.excludedTerms) {
    const excludedKeywords = []
    for (let i = 0; i < setting.excludedTerms.length; i += 1) {
      if (setting.excludedTerms[i].terms) {
        excludedKeywords.push(setting.excludedTerms[i].terms)
      }
    }
    filter.excludedKeywords = excludedKeywords
  }

  if (setting.themeInfos) {
    filter.themeIds = []
    for (let i = 0; i < setting.themeInfos.length; i += 1) {
      if (setting.themeInfos[i].themeInfos) {
        filter.themeIds.push(getIds(setting.themeInfos[i].themeInfos))
      }
    }
  }

  if (setting.includedSentimentDriver) {
    filter.includedSentimentDriver = JSON.parse(JSON.stringify(setting.includedSentimentDriver))
  }
  if (setting.excludedSentimentDriver) {
    filter.excludedSentimentDriver = JSON.parse(JSON.stringify(setting.excludedSentimentDriver))
  }
  if (setting.includedGeographies) {
    filter.countries = JSON.parse(JSON.stringify(setting.includedGeographies))
  }
  if (setting.excludedGeographies) {
    filter.excludedCountries = JSON.parse(JSON.stringify(setting.excludedGeographies))
  }

  if (setting.postTypeGroups) {
    filter.postTypeGroups = setting.postTypeGroups.slice()
  }

  if (setting.authorTypeGroups) {
    filter.authorTypeGroups = setting.authorTypeGroups.slice()
  }

  if (setting.ownedEarned) {
    filter.ownedEarned = setting.ownedEarned.slice()
  }

  if (setting.organicPaidPostTypes) {
    filter.organicPaidPostTypes = setting.organicPaidPostTypes.slice()
  }

  if (setting.verifiedUsers) {
    filter.verifiedUsers = setting.verifiedUsers.slice()
  }

  if (setting.followers) {
    filter.followers = setting.followers.slice()
  }

  if (setting.includedProfessions) {
    filter.includedProfessions = [setting.includedProfessions.slice()]
  }

  if (setting.excludedProfessions) {
    filter.excludedProfessions = [setting.excludedProfessions.slice()]
  }

  if (setting.includedEmojis) {
    filter.includedEmojis = [setting.includedEmojis.slice()]
  }

  if (setting.excludedEmojis) {
    filter.excludedEmojis = [setting.excludedEmojis.slice()]
  }

  if (!!setting.includedBioTexts && setting.includedBioTexts.length > 0) {
    filter.includedBioTexts = cloneDeep(setting.includedBioTexts)
  }

  if (!!setting.excludedBioTexts && setting.excludedBioTexts.length > 0) {
    filter.excludedBioTexts = cloneDeep(setting.excludedBioTexts)
  }

  if (setting.removedInsights) {
    filter.removedInsights = setting.removedInsights.slice()
  }

  if (setting.mediaTypes) {
    filter.mediaTypes = setting.mediaTypes.slice()
  }

  if (setting.dayHours) {
    filter.dayHours = [...setting.dayHours]
  }

  if (setting.geoFencings) {
    filter.geoFencings = cloneDeep(setting.geoFencings)
  }

  if (setting.cvLogos) {
    filter.cvLogos = JSON.parse(JSON.stringify(setting.cvLogos))
  }

  if (setting.primaryTerms) {
    filter.primaryTerms = [...setting.primaryTerms]
  }

  if (setting.includedChannels) {
    filter.includedChannels = setting.includedChannels.slice()
  }

  if (setting.excludedChannels) {
    filter.excludedChannels = setting.excludedChannels.slice()
  }

  if (!!setting.includedProductHierarchy && setting.includedProductHierarchy.length > 0) {
    filter.includedProductHierarchy = cloneDeep(setting.includedProductHierarchy)
  }
  if (!!setting.excludedProductHierarchy && setting.excludedProductHierarchy.length > 0) {
    filter.excludedProductHierarchy = cloneDeep(setting.excludedProductHierarchy)
  }

  if (setting.starRatings) {
    filter.starRatings = setting.starRatings.slice()
  }
  if (setting.surveyScores) {
    filter.surveyScores = setting.surveyScores.slice()
  }
  if (setting.netPromoterScores) {
    filter.netPromoterScores = setting.netPromoterScores.slice()
  }
  // @ts-ignore excludedStarRatings will be used when side by side widget is implemented.
  if (setting.excludedStarRatings) {
    // @ts-ignore excludedStarRatings will be used when side by side widget is implemented.
    filter.excludedStarRatings = setting.excludedStarRatings.slice()
  }
  // @ts-ignore excludedSurveyScores will be used when side by side widget is implemented.
  if (setting.excludedSurveyScores) {
    // @ts-ignore excludedSurveyScores will be used when side by side widget is implemented.
    filter.excludedSurveyScores = setting.excludedSurveyScores.slice()
  }
  // @ts-ignore excludedNetPromoterScores will be used when side by side widget is implemented.
  if (setting.excludedNetPromoterScores) {
    // @ts-ignore excludedNetPromoterScores will be used when side by side widget is implemented.
    filter.excludedNetPromoterScores = setting.excludedNetPromoterScores.slice()
  }
  if (setting.lxnxFilter) {
    if (setting.lxnxFilter.includedSources) {
      filter.lxnxFilter.includedSources = setting.lxnxFilter.includedSources.slice()
    }
    if (setting.lxnxFilter.excludedSources) {
      filter.lxnxFilter.excludedSources = setting.lxnxFilter.excludedSources.slice()
    }
    if (setting.lxnxFilter.includedSubjects) {
      filter.lxnxFilter.includedSubjects = JSON.parse(
        JSON.stringify(setting.lxnxFilter.includedSubjects)
      )
    }
    if (setting.lxnxFilter.excludedSubjects) {
      filter.lxnxFilter.excludedSubjects = JSON.parse(
        JSON.stringify(setting.lxnxFilter.excludedSubjects)
      )
    }
    if (setting.lxnxFilter.sourceTypeCategories) {
      filter.lxnxFilter.sourceTypeCategories = setting.lxnxFilter.sourceTypeCategories.slice()
    }
    if (setting.lxnxFilter.sourceTypeQualities) {
      filter.lxnxFilter.sourceTypeQualities = setting.lxnxFilter.sourceTypeQualities.slice()
    }
    if (setting.lxnxFilter.sourceTypeGenres) {
      filter.lxnxFilter.sourceTypeGenres = setting.lxnxFilter.sourceTypeGenres.slice()
    }
  }
  return filter
}

export function toDeltaFilter(filter: Filter): Filter {
  const deltaFilter = cloneDeep(filter)
  const startDate = filter.publishedDate!.start!
  const period = filter.publishedDate!.end! - startDate
  deltaFilter.publishedDate = DateRangeVo()
  deltaFilter.publishedDate.start = startDate - period
  deltaFilter.publishedDate.end = startDate - 1

  return deltaFilter
}

export function getDashboardId(logInfo: LogInfo): number | undefined {
  if (logInfo) {
    return logInfo.dashboardId
  }
  return -1
}

export function rounding(value: number, places: number): number {
  if (Number.isNaN(value) || value === 0) {
    return 0
  }
  const multiplier = 10 ** places
  const result = Math.round(value * multiplier) / multiplier
  if (Math.abs(result) < 1 && places === 0) {
    return 0
  }
  return result
}
